import {store} from '../../App';
import * as types from '../actions/actionTypes';
import {
  dispatchLoadJbaShipTos,
  dispatchLoadShipTos,
} from '../actions/checkoutActions';
import {
  AdditionalInfoData,
  CheckoutState,
  ShippingData,
} from '../reducers/checkoutReducer';

const CHECKOUT_DRAFT_STORAGE_PREFIX = 'nts-checkout-draft:';

export interface CheckoutDraft {
  additionalInfoData?: AdditionalInfoData;
  jbaShippingData?: ShippingData;
  sapShippingData?: ShippingData;
  jbaAccountOverride?: string;
  jbaShipToData?: CheckoutState['jbaShipToData'];
  shipToData?: CheckoutState['shipToData'];
}

export function getCheckoutCartIdFromLocation(): string | null {
  const match = window.location.pathname.match(/\/checkout\/(?:order|review)\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function getDraftStorageKey(cartId: string): string {
  return `${CHECKOUT_DRAFT_STORAGE_PREFIX}${cartId}`;
}

export function buildCheckoutDraft(checkoutState: CheckoutState): CheckoutDraft {
  return {
    additionalInfoData: checkoutState.additionalInfoData,
    jbaShippingData: checkoutState.jbaCartData.shippingData,
    sapShippingData: checkoutState.sapCartData.shippingData,
    jbaAccountOverride: checkoutState.jbaAccountOverride,
    jbaShipToData: checkoutState.jbaShipToData,
    shipToData: checkoutState.shipToData,
  };
}

export function hasCheckoutDraftData(draft: CheckoutDraft): boolean {
  const hasAdditionalInfo = !!(
    draft.additionalInfoData &&
    Object.keys(draft.additionalInfoData).some((key) => {
      const value = draft.additionalInfoData![key as keyof AdditionalInfoData];
      return value !== undefined && value !== '';
    })
  );

  return !!(
    hasAdditionalInfo ||
    draft.jbaShippingData ||
    draft.sapShippingData ||
    draft.jbaAccountOverride ||
    (draft.jbaShipToData && Object.keys(draft.jbaShipToData).length > 0) ||
    (draft.shipToData && Object.keys(draft.shipToData).length > 0)
  );
}

export function saveCheckoutDraft(cartId: string, checkoutState: CheckoutState): void {
  try {
    const draft = buildCheckoutDraft(checkoutState);
    const storageKey = getDraftStorageKey(cartId);

    if (!hasCheckoutDraftData(draft)) {
      sessionStorage.removeItem(storageKey);
      return;
    }

    sessionStorage.setItem(storageKey, JSON.stringify(draft));
  } catch {
    // sessionStorage may be unavailable (private browsing, quota, etc.)
  }
}

export function loadCheckoutDraft(cartId: string): CheckoutDraft | null {
  try {
    const raw = sessionStorage.getItem(getDraftStorageKey(cartId));
    if (!raw) {
      return null;
    }

    return JSON.parse(raw) as CheckoutDraft;
  } catch {
    return null;
  }
}

export function clearCheckoutDraft(cartId: string): void {
  try {
    sessionStorage.removeItem(getDraftStorageKey(cartId));
  } catch {
    // ignore
  }
}

export function saveCheckoutDraftFromStore(cartId?: string): void {
  const id = cartId || getCheckoutCartIdFromLocation();
  if (!id) {
    return;
  }

  saveCheckoutDraft(id, store.getState().checkoutState);
}

export function restoreCheckoutDraft(cartId: string): boolean {
  const draft = loadCheckoutDraft(cartId);
  if (!draft || !hasCheckoutDraftData(draft)) {
    return false;
  }

  if (draft.shipToData && Object.keys(draft.shipToData).length > 0) {
    store.dispatch(dispatchLoadShipTos(draft.shipToData));
  }

  if (draft.jbaShipToData && Object.keys(draft.jbaShipToData).length > 0) {
    store.dispatch(dispatchLoadJbaShipTos(draft.jbaShipToData));
  }

  if (draft.jbaAccountOverride) {
    store.dispatch({
      type: types.JBA_ACCOUNT_OVERRIDE,
      jbaAccountNum: draft.jbaAccountOverride,
    });
  }

  if (draft.sapShippingData) {
    store.dispatch({
      type: types.UPDATE_SHIPPING_DATA,
      data: {
        cartType: 'sapCartData',
        shippingData: draft.sapShippingData,
      },
    });
  }

  if (draft.jbaShippingData) {
    store.dispatch({
      type: types.UPDATE_SHIPPING_DATA,
      data: {
        cartType: 'jbaCartData',
        shippingData: draft.jbaShippingData,
      },
    });
  }

  if (draft.additionalInfoData && Object.keys(draft.additionalInfoData).length > 0) {
    store.dispatch({
      type: types.UPDATE_ADDITIONAL_INFO_DATA,
      data: draft.additionalInfoData,
    });
  }

  return true;
}
